import { useMemo } from 'react';

import { useGetMediaOfferGroupsQuery } from 'services';

/**
 * @description Verilen birim bu medya teklifi grubuna karar vermiş mi?
 *
 * BE `currentVersion.decisions[]` ve `lastDecision`'ı **grup genelinde** hesaplar (tüm versiyonların
 * kararları — BE `MediaOffer::getGroupDecisions`). Revize kararı yeni versiyona kopyalamaz ama önceki
 * karar yanıtta görünmeye devam eder; yani içerik düzeltmesi birimin kararını düşürmez.
 *
 * `decisionType` ayrıca kontrol edilir — BE boş bir karar objesi döndürürse yanlış pozitif olmasın
 * (aynı savunma `ArticleOrderMedia/index.jsx` `historyGroups` hesabında da var).
 *
 * @param {Object|null} group - `media_offer_groups` yanıtındaki grup (`currentVersion` gömülü).
 * @param {string|null} decider - `MEDIA_OFFER_DECIDER` değerlerinden biri (aranan karar birimi).
 * @returns {boolean} Bu birim gruba karar vermişse `true`.
 */
export const hasDecisionFromDecider = (group, decider) => {
  const version = group?.currentVersion;
  if (!version || !decider) return false;

  // Yanıt `decisions[]` (gruba verilmiş TÜM kararlar) taşıyorsa onu tararız: bir üst birim
  // sonradan karar verdiğinde `lastDecision` değişir ve bizim kararımızı gölgeleyebilir.
  // Taşımıyorsa `lastDecision` tek kaynaktır (grup listesi yanıtının garanti ettiği alan).
  const decisions = Array.isArray(version.decisions) ? version.decisions : [version.lastDecision];

  return decisions.some((decision) => decision?.decidedBy === decider && Boolean(decision?.decisionType));
};

/**
 * @typedef {Object} MediaOfferDecisionGuard
 * @property {number} undecidedCount - Aktif aşamanın kararı verilmemiş medya teklifi grubu sayısı.
 * @property {string|null} blockReason - Aşama onayının neden engellendiğini anlatan, doğrudan
 *   gösterilebilir metin; engel yoksa `null`. Çağıran taraf bunu buton tooltip'i olarak basar ve
 *   `Boolean(blockReason)` ile butonu kapatır.
 */

/**
 * @description Aşama onayı öncesi "karar verilmemiş medya teklifi kalmasın" kuralını hesaplar.
 *
 * NEDEN: Karar zorunlu aşamada kullanıcı eksik kararla aşamayı geçirmesin; engel **gönderim
 * öncesinde** bildirilir, buton baştan kapanır. Tek zorunlu aşama 36'dır (AB - Medya Teklifi
 * Kontrolü); BE de kuralı yalnız orada `400` ile zorlar (`AssignmentDecideDenormalizer`).
 *
 * NASIL — statü listesi tutulmaz: zorunluluk `useMediaOfferPermission`'ın `decisionRequired`
 * bayrağından okunur (tek kaynak `STATUS_RULE`). Zorunluluk aşama bazında değişirse yalnız o tablo
 * güncellenir, burada değişiklik gerekmez.
 *
 * MALİYET: Sorgu, medya teklifi tablosunun (`ArticleOrderMedia`) kullandığı sorgunun aynısıdır;
 * RTK Query aynı argümanla tek istek attığı için bu hook ek network isteği üretmez.
 *
 * SINIR: Veri gelmeden (ya da `skip` koşullarında) engel bildirilmez — 36'da o kısa aralıkta
 * gönderilirse BE `400` döner ve mesaj `useApiErrorMessage` üzerinden görünür.
 *
 * @param {Object} params
 * @param {number|string|null} params.offerID - `ArticleOffer` id'si.
 * @param {string|null} params.activeDecider - Aktif aşamanın karar birimi
 *   (`useMediaOfferPermission(offer).activeDecider`).
 * @param {boolean} [params.decisionRequired=false] - Aktif aşama karar vermek zorunda mı
 *   (`useMediaOfferPermission(offer).decisionRequired`). Değilse engel hesaplanmaz.
 * @param {boolean} [params.canManage=false] - Current_user bu aşamada işlem yetkisine sahip mi
 *   (`useMediaOfferPermission(offer).canManage`). Yetkisi yoksa onaylayamayacağı için hesap yapılmaz.
 * @returns {MediaOfferDecisionGuard} Engel bilgisi.
 */
export const useMediaOfferDecisionGuard = ({ offerID, activeDecider, decisionRequired = false, canManage = false }) => {
  const skip = !offerID || !canManage || !decisionRequired;

  const { data: groups } = useGetMediaOfferGroupsQuery({ articleOffer: offerID }, { skip });

  return useMemo(() => {
    if (skip) return { undecidedCount: 0, blockReason: null };

    const undecidedCount = (groups ?? []).filter((group) => !hasDecisionFromDecider(group, activeDecider)).length;

    return {
      undecidedCount,
      blockReason:
        undecidedCount > 0
          ? `Karar verilmemiş ${undecidedCount} medya teklifi var. Aşamayı onaylamadan önce tüm medya tekliflerine "Uygundur" / "Uygun Değildir" kararı veriniz.`
          : null,
    };
  }, [skip, groups, activeDecider]);
};
